export const DEFAULT_MIN_LABEL_PERCENTAGE = 5;

const DEFAULT_LEGEND_OPTIONS = {
  display: true,
  position: 'right',
  labels: {
    boxWidth: 12,
    padding: 15,
    font: {
      size: 12,
    },
  },
};

const DEFAULT_TOOLTIP_CALLBACKS = {
  label(context) {
    const label = context.label || '';
    const value = context.formattedValue || '';
    return `${label}: ${value}`;
  },
};

const DEFAULT_LAYOUT_PADDING = {
  top: 48,
  right: 36,
  bottom: 48,
  left: 36,
};

const HTML_LEGEND_LAYOUT_PADDING = {
  top: 40,
  right: 28,
  bottom: 40,
  left: 28,
};

const toNumericValue = value => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

export const isDataPointVisible = (chart, dataIndex) => {
  if (typeof chart?.getDataVisibility === 'function') {
    return chart.getDataVisibility(dataIndex);
  }

  return true;
};

export const getVisibleDatasetTotal = (chart, datasetIndex = 0) => {
  const dataset = chart?.data?.datasets?.[datasetIndex];
  const data = dataset?.data || [];

  return data.reduce((total, value, dataIndex) => {
    const numericValue = toNumericValue(value);

    if (!isDataPointVisible(chart, dataIndex) || numericValue <= 0) {
      return total;
    }

    return total + numericValue;
  }, 0);
};

export const getSlicePercentage = ({
  chart,
  datasetIndex = 0,
  dataIndex,
}) => {
  const value = toNumericValue(
    chart?.data?.datasets?.[datasetIndex]?.data?.[dataIndex]
  );

  if (!isDataPointVisible(chart, dataIndex) || value <= 0) {
    return 0;
  }

  const visibleTotal = getVisibleDatasetTotal(chart, datasetIndex);

  if (!visibleTotal) {
    return 0;
  }

  return (value / visibleTotal) * 100;
};

export const shouldRenderSliceArtifacts = ({
  chart,
  datasetIndex = 0,
  dataIndex,
  minPercentage = DEFAULT_MIN_LABEL_PERCENTAGE,
}) => {
  return (
    getSlicePercentage({ chart, datasetIndex, dataIndex }) >= minPercentage
  );
};

export const getDatalabelDisplay = (
  context,
  minPercentage = DEFAULT_MIN_LABEL_PERCENTAGE
) => {
  return shouldRenderSliceArtifacts({
    chart: context.chart,
    datasetIndex: context.datasetIndex,
    dataIndex: context.dataIndex,
    minPercentage,
  })
    ? 'auto'
    : false;
};

export const createConnectorLinesPlugin = ({
  minPercentage = DEFAULT_MIN_LABEL_PERCENTAGE,
} = {}) => ({
  id: 'connectorLines',
  afterDatasetsDraw(chart) {
    const dataset = chart?.data?.datasets?.[0];
    const meta = chart?.getDatasetMeta?.(0);

    if (!dataset || !meta?.data?.length) {
      return;
    }

    const ctx = chart.ctx;

    meta.data.forEach((arc, dataIndex) => {
      if (
        !shouldRenderSliceArtifacts({
          chart,
          dataIndex,
          minPercentage,
        })
      ) {
        return;
      }

      const angle = (arc.startAngle + arc.endAngle) / 2;
      const radialOffset = 24;
      const horizontalOffset = 16;
      const x1 = arc.x + Math.cos(angle) * arc.outerRadius;
      const y1 = arc.y + Math.sin(angle) * arc.outerRadius;
      const x2 = arc.x + Math.cos(angle) * (arc.outerRadius + radialOffset);
      const y2 = arc.y + Math.sin(angle) * (arc.outerRadius + radialOffset);
      const x3 = x2 + (Math.cos(angle) >= 0 ? horizontalOffset : -horizontalOffset);
      const y3 = y2;
      const lineColor =
        dataset.borderColor?.[dataIndex] ||
        dataset.backgroundColor?.[dataIndex] ||
        '#999';

      ctx.save();
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 2]);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.lineTo(x3, y3);
      ctx.stroke();
      ctx.restore();
    });
  },
});

const getOrCreateLegendList = legendContainer => {
  let listContainer = legendContainer.querySelector('ul');

  if (listContainer) {
    listContainer.replaceChildren();
    return listContainer;
  }

  listContainer = document.createElement('ul');
  listContainer.style.display = 'grid';
  listContainer.style.gridTemplateColumns =
    'repeat(auto-fit, minmax(12rem, 1fr))';
  listContainer.style.gap = '0.5rem 0.75rem';
  listContainer.style.listStyle = 'none';
  listContainer.style.margin = '0';
  listContainer.style.padding = '0';
  legendContainer.appendChild(listContainer);

  return listContainer;
};

export const createHtmlLegendPlugin = getContainer => ({
  id: 'htmlLegend',
  afterUpdate(chart) {
    const legendContainer = getContainer();

    if (!legendContainer) {
      return;
    }

    const labelsGenerator =
      chart?.options?.plugins?.legend?.labels?.generateLabels;

    if (typeof labelsGenerator !== 'function') {
      return;
    }

    const listContainer = getOrCreateLegendList(legendContainer);
    const items = labelsGenerator(chart);

    items.forEach(item => {
      const listItem = document.createElement('li');
      const button = document.createElement('button');
      const colorBox = document.createElement('span');
      const label = document.createElement('span');

      button.type = 'button';
      button.dataset.legendIndex = String(item.index);
      button.style.alignItems = 'center';
      button.style.background = 'transparent';
      button.style.border = 'none';
      button.style.cursor = 'pointer';
      button.style.display = 'flex';
      button.style.gap = '0.5rem';
      button.style.margin = '0';
      button.style.maxWidth = '100%';
      button.style.padding = '0';
      button.style.textAlign = 'left';
      button.onclick = () => {
        const { type } = chart.config;

        if (type === 'pie' || type === 'doughnut') {
          chart.toggleDataVisibility(item.index);
        } else {
          chart.setDatasetVisibility(
            item.datasetIndex,
            !chart.isDatasetVisible(item.datasetIndex)
          );
        }

        chart.update();
      };

      colorBox.style.background = item.fillStyle;
      colorBox.style.border = `${item.lineWidth}px solid ${item.strokeStyle}`;
      colorBox.style.borderRadius = '9999px';
      colorBox.style.display = 'inline-block';
      colorBox.style.flexShrink = '0';
      colorBox.style.height = '0.75rem';
      colorBox.style.marginTop = '0.125rem';
      colorBox.style.width = '0.75rem';

      label.textContent = item.text;
      label.style.color = item.fontColor;
      label.style.display = 'block';
      label.style.fontSize = '0.875rem';
      label.style.lineHeight = '1.25rem';
      label.style.overflow = 'hidden';
      label.style.textDecoration = item.hidden ? 'line-through' : 'none';
      label.style.textOverflow = 'ellipsis';
      label.style.whiteSpace = 'nowrap';

      button.appendChild(colorBox);
      button.appendChild(label);
      listItem.appendChild(button);
      listContainer.appendChild(listItem);
    });
  },
});

export const buildDoughnutChartOptions = ({
  chartOptions = {},
  minLabelPercentage = DEFAULT_MIN_LABEL_PERCENTAGE,
  hasHtmlLegend = false,
  onClick,
} = {}) => {
  const pluginOptions = chartOptions.plugins || {};
  const customLegendOptions = pluginOptions.legend || {};
  const customTooltipOptions = pluginOptions.tooltip || {};
  const customDatalabelOptions = pluginOptions.datalabels || {};
  const customLayout = chartOptions.layout || {};
  const customLayoutPadding = customLayout.padding || {};

  const legendDisplay = hasHtmlLegend ? false : customLegendOptions.display;

  return {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '60%',
    ...chartOptions,
    ...(onClick ? { onClick } : {}),
    plugins: {
      ...pluginOptions,
      legend: {
        ...DEFAULT_LEGEND_OPTIONS,
        ...customLegendOptions,
        display:
          typeof legendDisplay === 'boolean'
            ? legendDisplay
            : DEFAULT_LEGEND_OPTIONS.display,
        labels: {
          ...DEFAULT_LEGEND_OPTIONS.labels,
          ...(customLegendOptions.labels || {}),
        },
      },
      tooltip: {
        ...customTooltipOptions,
        callbacks: {
          ...DEFAULT_TOOLTIP_CALLBACKS,
          ...(customTooltipOptions.callbacks || {}),
        },
      },
      datalabels: {
        display(context) {
          return getDatalabelDisplay(context, minLabelPercentage);
        },
        color: '#374151',
        font: {
          size: 12,
          weight: '600',
        },
        anchor: 'end',
        align: 'end',
        offset: 18,
        formatter(_value, context) {
          const percentage = getSlicePercentage({
            chart: context.chart,
            datasetIndex: context.datasetIndex,
            dataIndex: context.dataIndex,
          });

          return `${percentage.toFixed(1)}%`;
        },
        textAlign: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.96)',
        borderColor(context) {
          return (
            context.dataset.backgroundColor?.[context.dataIndex] ||
            context.dataset.borderColor?.[context.dataIndex] ||
            '#999'
          );
        },
        borderRadius: 4,
        borderWidth: 1.5,
        padding: {
          top: 3,
          bottom: 3,
          left: 6,
          right: 6,
        },
        clamp: true,
        clip: false,
        ...customDatalabelOptions,
      },
    },
    layout: {
      ...customLayout,
      padding: {
        ...(hasHtmlLegend ? HTML_LEGEND_LAYOUT_PADDING : DEFAULT_LAYOUT_PADDING),
        ...customLayoutPadding,
      },
    },
  };
};
